import { create } from 'zustand'
import { queryClient } from '@/shared/lib/queryClientSingleton'
import { disconnectChat } from '@/shared/lib/chatWebSocket'

interface TeamMember {
  id: number
  name: string
  email: string
  roles: string[]
  termsAcceptedAt?: string | null
  orgId?: number
  orgSlug?: string
  orgName?: string
}

interface AuthState {
  accessToken: string | null
  teamMember: TeamMember | null
  setAuth: (token: string, member: TeamMember) => void
  /**
   * An authenticated identity with NO studio session (slice 6): the token
   * carries no org claim and reaches only the identity-only surface listed in
   * SecurityConfig. Deliberately does NOT persist anything — there is no
   * refresh cookie behind an identity-only login either, so a reload correctly
   * lands the person back on the sign-in page rather than on a dead session.
   */
  setIdentitySession: (token: string) => void
  updateMember: (updates: Partial<TeamMember>) => void
  clearAuth: () => void
}

const MEMBER_KEY = 'ps_member'

function loadMember(): TeamMember | null {
  try {
    const raw = localStorage.getItem(MEMBER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  teamMember: loadMember(),
  setAuth: (token, member) => {
    set((state) => {
      const prev = state.teamMember
      // Cross-tenant cache leak guard. TanStack Query keys are resource-only
      // (see api/queryKeys.ts), never scoped by org, and both login and logout
      // are client-side navigations (no page reload). So when the authenticated
      // identity actually CHANGES — log out of org A, log in as org B in the same
      // tab — org A's cached /settings, /clients, /finance… would otherwise be
      // served inside org B's session. Wipe the cache on that change.
      //
      // Identity-change ONLY: ProtectedRoute's cold-boot /auth/refresh and the
      // 401 refresh interceptor (api/client.ts) both re-call setAuth with the
      // SAME member mid-session; clearing there would blow away a healthy cache
      // on every token refresh. A null prev (first login, or the moment right
      // after clearAuth on logout) needs no clear here — the cache is already
      // empty on cold load, and on logout clearAuth() owns the wipe. Keeping this
      // branch dependent on a real previous identity is what makes clearAuth()'s
      // logout wipe load-bearing (and its e2e regression test meaningful).
      if (prev && (prev.id !== member.id || prev.orgId !== member.orgId)) {
        queryClient.clear()
      }
      localStorage.setItem(MEMBER_KEY, JSON.stringify(member))
      return { accessToken: token, teamMember: member }
    })
  },
  setIdentitySession: (token) => {
    // Same cross-tenant reasoning as setAuth's wipe, one step stricter: an
    // identity-only session must never be able to read whatever the previous
    // org session left in the cache, and there is no member id to compare, so
    // the wipe is unconditional.
    localStorage.removeItem(MEMBER_KEY)
    set({ accessToken: token, teamMember: null })
    queryClient.clear()
  },
  updateMember: (updates) => {
    set((state) => {
      if (!state.teamMember) return state
      const updated = { ...state.teamMember, ...updates }
      localStorage.setItem(MEMBER_KEY, JSON.stringify(updated))
      return { teamMember: updated }
    })
  },
  clearAuth: () => {
    localStorage.removeItem(MEMBER_KEY)
    set({ accessToken: null, teamMember: null })
    // Cross-tenant cache leak guard: drop all cached + in-flight queries on
    // logout so the next person to sign in on the same tab (a shared studio
    // machine) can never be served the previous org's cached data. Unconditional
    // — logout always means teardown. Runs after wiping token/member so no
    // interceptor can refire an authenticated request against the dead cache.
    queryClient.clear()
    // The chat socket now outlives ChatPage (see its mount effect), so logout is
    // the only place left that can close it. Same shared-machine reasoning as the
    // cache wipe above: an authenticated STOMP session must not survive into the
    // next person's session.
    disconnectChat()
  },
}))
